import React, { useEffect, useState, useRef } from 'react';
import { Group, Text, useComputedColorScheme } from '@mantine/core';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import {
  Competency,
  Swimlane,
  CompetencyMapData,
  UserCompetency,
  Grade,
  User,
  CardSize,
} from '../../types';
import { CompetencyCard } from './CompetencyCard';
import { CompetencyDrawer } from './CompetencyDrawer';
import { CompetencyCreateModal } from './CompetencyCreateModal';
import { GradeHeader } from './GradeHeader';
import { CompetencyMapMobile } from './CompetencyMapMobile';
import { SwimlaneTransition } from './SwimlaneTransition';
import { normalizeSortOrder } from '../../utils/data';
import { showToast } from '../../utils/toast';
import { useDevice } from '../../hooks/useDevice';
import { useTranslation } from 'react-i18next';
import { useTags } from '../../hooks/useReferenceApi';
import { useSaveMapLayout } from '../../hooks/useCompetencyApi';
import { usePermission } from '../../hooks/usePermission';

interface CompetencyMapProps {
  isDragDropEnabled: boolean;
  cardSize: CardSize;
  currentGrade?: Grade;
  nextGrade?: Grade;
  competencyMapData?: CompetencyMapData;
  competencyMapId?: string;
  userCompetencies?: UserCompetency[];
  currentUser?: User;
  hideGradeProgress?: boolean;
  // Скрыть write-действия дровера (submit-for-review / grant). Для framework-вида
  // чужой карты, где актором выступает залогиненный пользователь.
  hideStatusAndActions?: boolean;
  /** Карта-пример: без правки компетенций даже у админа. */
  isExample?: boolean;
}

export const CompetencyMap: React.FC<CompetencyMapProps> = ({
  isDragDropEnabled,
  cardSize,
  currentGrade,
  nextGrade,
  competencyMapData,
  competencyMapId,
  userCompetencies = [],
  currentUser,
  hideGradeProgress = false,
  hideStatusAndActions = false,
  isExample = false,
}) => {
  // Права — у вошедшего пользователя, а не у того, чья карта показана (currentUser).
  // Править карту — право maps.manage (владелец без флага admin и «Настройщик» тоже).
  const canManageMaps = usePermission('maps.manage');
  const isAdmin = canManageMaps && !isExample;
  const deviceType = useDevice();
  const { t } = useTranslation();
  const { data: tagDict = [] } = useTags();
  const saveLayout = useSaveMapLayout();
  const [data, setData] = useState<CompetencyMapData | null>(null);
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);
  const [, setIsLoading] = useState(true);
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState<number>(0);
  const [collapsedGrades, setCollapsedGrades] = useState<Set<number>>(new Set());
  const [collapsedSwimlanes, setCollapsedSwimlanes] = useState<Set<number>>(new Set());
  const [animatingGrades, setAnimatingGrades] = useState<Set<number>>(new Set());
  const [visibleGrades, setVisibleGrades] = useState<Set<number>>(new Set());
  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(null);
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';

  function updateHeight() {
    if (gridRef.current) {
      setGridHeight(gridRef.current.offsetHeight);
    }
  }

  useEffect(() => {
    if (competencyMapData) {
      // Используем только переданные данные с бэкенда
      const normalizedData = {
        ...competencyMapData,
        grades: normalizeSortOrder(competencyMapData.grades),
      };
      setData(normalizedData);
      setVisibleGrades(new Set(competencyMapData.grades.map((_: Grade, idx: number) => idx)));
      setIsLoading(false);
    } else {
      // Если данных нет, показываем пустое состояние
      setData(null);
      setIsLoading(false);
    }
  }, [competencyMapData]);

  useEffect(() => {
    setTimeout(() => {
      updateHeight();
    }, 300);
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    updateHeight();
    let counter = 0;
    const interval = setInterval(() => {
      counter++;
      if (counter === 9) clearInterval(interval);
      updateHeight();
    }, 50);
  }, [collapsedSwimlanes]);

  const toggleSwimlaneCollapse = (swimlaneIdx: number) => {
    const isCurrentlyCollapsed = collapsedSwimlanes.has(swimlaneIdx);

    if (isCurrentlyCollapsed) {
      // Разворачиваем
      setCollapsedSwimlanes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(swimlaneIdx);
        return newSet;
      });
    } else {
      // Сворачиваем
      setCollapsedSwimlanes((prev) => new Set(prev).add(swimlaneIdx));
    }
  };

  const toggleGradeCollapse = (gradeIdx: number) => {
    const isCurrentlyCollapsed = collapsedGrades.has(gradeIdx);

    // Добавляем грейд в анимацию
    setAnimatingGrades((prev) => new Set(prev).add(gradeIdx));

    if (isCurrentlyCollapsed) {
      // Разворачиваем - сначала меняем размер, потом показываем компетенции
      setCollapsedGrades((prev) => {
        const newSet = new Set(prev);
        newSet.delete(gradeIdx);
        return newSet;
      });
      // Показываем компетенции с небольшой задержкой для плавности
      setTimeout(() => {
        setVisibleGrades((prev) => new Set(prev).add(gradeIdx));
      }, 150);
    } else {
      // Сворачиваем - сразу скрываем компетенции
      setCollapsedGrades((prev) => new Set(prev).add(gradeIdx));
      setVisibleGrades((prev) => {
        const newSet = new Set(prev);
        newSet.delete(gradeIdx);
        return newSet;
      });
    }

    // Через 300ms убираем из анимации
    setTimeout(() => {
      setAnimatingGrades((prev) => {
        const newSet = new Set(prev);
        newSet.delete(gradeIdx);
        return newSet;
      });
    }, 300);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !data) return;

    // droppableId = gradeIdx__swimlaneId
    const [fromGradeIdx, fromSwimlaneId] = result.source.droppableId.split('__');
    const [toGradeIdx, toSwimlaneId] = result.destination.droppableId.split('__');
    const fromGrade = data.grades[+fromGradeIdx];
    const toGrade = data.grades[+toGradeIdx];
    const compId = result.draggableId;

    // Найти компетенцию и её индекс в fromGrade
    const fromComps = fromGrade.competencies;
    const compIdx = fromComps.findIndex((c) => c._id === compId && c.swimlaneId === fromSwimlaneId);
    if (compIdx === -1) return;
    const comp = fromComps[compIdx];

    // Подготовить новую компетенцию (обновить swimlaneId если перенос между swimlane)
    const newComp: Competency = {
      ...comp,
      swimlaneId: toSwimlaneId,
    };

    // Получить компетенции целевого свимлейна в целевом грейде (исключая перемещаемую)
    const targetComps = toGrade.competencies.filter(
      (c) => c.swimlaneId === toSwimlaneId && c._id !== compId,
    );

    // Определить новый sortOrder в зависимости от позиции
    let newSortOrder: number;
    if (result.destination.index === 0) {
      // Первая позиция - ставим на 5 меньше чем у первой существующей
      newSortOrder =
        targetComps.length > 0 && targetComps[0]?.sortOrder ? targetComps[0].sortOrder - 5 : 10;
    } else if (result.destination.index >= targetComps.length) {
      // Последняя позиция - ставим на 5 больше чем у последней существующей
      const lastComp = targetComps[targetComps.length - 1];
      newSortOrder = targetComps.length > 0 && lastComp?.sortOrder ? lastComp.sortOrder + 5 : 10;
    } else {
      // Между двумя компетенциями - берем среднее значение
      const prevComp = targetComps[result.destination.index - 1];
      const nextComp = targetComps[result.destination.index];
      if (prevComp?.sortOrder && nextComp?.sortOrder) {
        newSortOrder = Math.floor((prevComp.sortOrder + nextComp.sortOrder) / 2);
      } else {
        newSortOrder = 10;
      }
    }

    newComp.sortOrder = newSortOrder;

    // Проверяем, перетаскиваем ли мы внутри одного грейда и свимлейна
    const isSameGradeAndSwimlane = +fromGradeIdx === +toGradeIdx && fromSwimlaneId === toSwimlaneId;

    // Обновить массивы компетенций в грейдах
    const newGrades = data.grades.map((g, idx) => {
      if (isSameGradeAndSwimlane) {
        // Если перетаскиваем внутри одного грейда и свимлейна, просто обновляем sortOrder
        if (idx === +fromGradeIdx) {
          const newComps = [...g.competencies];
          newComps[compIdx] = newComp;
          return { ...g, competencies: newComps };
        }
      } else {
        // Если перетаскиваем между разными грейдами или свимлейнами
        if (idx === +fromGradeIdx) {
          // Удалить компетенцию из исходного грейда
          const newComps = [...g.competencies];
          newComps.splice(compIdx, 1);
          return { ...g, competencies: newComps };
        }
        if (idx === +toGradeIdx) {
          // Добавить компетенцию в целевой грейд
          const newComps = [...g.competencies, newComp];
          return { ...g, competencies: newComps };
        }
      }
      return g;
    });

    // Нормализовать сортировку только в целевом грейде и свимлейне
    const normalizedGrades = newGrades.map((grade, idx) => {
      if (idx === +toGradeIdx) {
        // Нормализуем только компетенции целевого свимлейна в целевом грейде
        const normalizedComps = grade.competencies.map((comp) => {
          if (comp.swimlaneId === toSwimlaneId) {
            // Найти позицию этой компетенции среди компетенций целевого свимлейна
            const swimlaneComps = grade.competencies
              .filter((c) => c.swimlaneId === toSwimlaneId)
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
            const compIndex = swimlaneComps.findIndex((c) => c._id === comp._id);
            return { ...comp, sortOrder: (compIndex + 1) * 10 };
          }
          return comp;
        });
        return { ...grade, competencies: normalizedComps };
      }
      return grade;
    });

    const previousData = data;
    setData({ ...data, grades: normalizedGrades });
    if (!competencyMapId) return;

    // Сохраняем целевую ячейку целиком: в ней перенумерован порядок.
    const links = normalizedGrades[+toGradeIdx].competencies
      .filter((c) => c.swimlaneId === toSwimlaneId)
      .map((c) => ({
        competencyId: c._id,
        gradeId: toGrade._id,
        swimlaneId: toSwimlaneId,
        order: c.sortOrder ?? 0,
      }));
    saveLayout.mutate(
      { mapId: competencyMapId, links },
      {
        onSuccess: () =>
          showToast({
            title: t('competencyMaps.layoutSaved'),
            message: t('competencyMaps.layoutSavedMessage'),
            color: 'teal',
            icon: <IconCheck size={16} />,
          }),
        onError: () => {
          setData(previousData);
          showToast({
            title: t('common.error'),
            message: t('competencyMaps.layoutSaveError'),
            color: 'red',
          });
        },
      },
    );
  };

  if (!data) {
    return (
      <div style={{ padding: 32 }}>
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  // Сетка: первая строка — грейды, первый столбец — свимлейны, последняя колонка для скроллбара
  const gridTemplateColumns =
    data.grades.map((_, gradeIdx) => (collapsedGrades.has(gradeIdx) ? '60px' : '240px')).join(' ') +
    ' 1px';

  const maxHeight = 'calc(100svh - 190px)';

  // Если мобильное устройство, показываем мобильную версию
  if (deviceType === 'mobile') {
    return (
      <CompetencyMapMobile
        isExample={isExample}
        data={data}
        competencyMapId={competencyMapId || ''}
        currentGradeId={currentGrade?._id}
        nextGrade={nextGrade}
        cardSize={cardSize}
        userCompetencies={userCompetencies}
        currentUser={currentUser}
        onCompetencyUpdate={(competency) => {
          // Обновляем компетенцию в локальном состоянии
          const updatedData = { ...data };
          const grade = updatedData.grades.find((g) =>
            g.competencies.some((c) => c._id === competency._id),
          );
          if (grade) {
            grade.competencies = grade.competencies.map((c) =>
              c._id === competency._id ? competency : c,
            );
            setData(updatedData);
          }
        }}
        onCompetencyDelete={(competencyId) => {
          // Удаляем компетенцию из локального состояния
          const updatedData = { ...data };
          updatedData.grades.forEach((grade) => {
            grade.competencies = grade.competencies.filter((c) => c._id !== competencyId);
          });
          setData(updatedData);
        }}
        onCompetencyCreate={(competency) => {
          // Добавляем новую компетенцию в локальное состояние
          const updatedData = { ...data };
          if (competency.swimlaneId) {
            // Находим грейд по компетенциям или используем первый
            const grade =
              updatedData.grades.find((g) =>
                g.competencies.some((c) => c.swimlaneId === competency.swimlaneId),
              ) || updatedData.grades[0];
            if (grade) {
              // Создаем объект Competency с временным _id
              const newCompetency: Competency = {
                _id: `temp_${Date.now()}`,
                title: competency.title,
                description: competency.description,
                swimlaneId: competency.swimlaneId,
                tags: (competency.tags || []).map((tg: string) => {
                  const found = tagDict.find((td) => td._id === tg);
                  return { _id: tg, name: found?.name ?? tg };
                }),
                mentors: competency.mentors.map((mentor) => {
                  if (typeof mentor === 'string') {
                    return {
                      _id: mentor,
                      name: '',
                      email: '',
                      position: '',
                    };
                  }
                  return mentor;
                }),
                sortOrder: grade.competencies.length,
              };
              grade.competencies.push(newCompetency);
              setData(updatedData);
            }
          }
        }}
        onSwimlaneUpdate={(swimlane) => {
          // Обновляем свимлейн в локальном состоянии
          const updatedData = { ...data };
          const swimlaneIndex = updatedData.swimlanes.findIndex((s) => s._id === swimlane._id);
          if (swimlaneIndex !== -1) {
            updatedData.swimlanes[swimlaneIndex] = swimlane;
            setData(updatedData);
          }
        }}
        onGradeUpdate={(grade) => {
          // Обновляем грейд в локальном состоянии
          const updatedData = { ...data };
          const gradeIndex = updatedData.grades.findIndex((g) => g._id === grade._id);
          if (gradeIndex !== -1) {
            updatedData.grades[gradeIndex] = grade;
            setData(updatedData);
          }
        }}
      />
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ overflow: 'auto', maxHeight, paddingBottom: 20, paddingRight: 16 }}>
        <DragDropContext onDragEnd={isDragDropEnabled ? onDragEnd : () => {}}>
          <div
            ref={gridRef}
            style={
              {
                display: 'grid',
                gridTemplateColumns,
                gap: 16,
                marginTop: -16,
                alignItems: 'start',
                paddingLeft: currentGrade && data?.grades[0]?._id === currentGrade._id ? 10 : 0,
                '--grid-height': gridHeight ? `${gridHeight}px` : maxHeight,
                transition: 'grid-template-columns 0.3s ease-in-out',
              } as React.CSSProperties
            }
          >
            {data.grades.map((grade, gradeIdx) => {
              const classes = [
                'grade-title-before',
                nextGrade && grade._id === nextGrade._id ? 'grade-title-before--next' : '',
                currentGrade && grade._id === currentGrade._id ? 'grade-title-before--current' : '',
                gradeIdx === data.grades.length - 1 ? 'grade-title-before--last' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return <div key={gradeIdx} className={classes} />;
            })}
            <div />
            {data.grades.map((grade, gradeIdx) => {
              const isCollapsed = collapsedGrades.has(gradeIdx);
              const isAnimating = animatingGrades.has(gradeIdx);

              return (
                <GradeHeader
                  key={gradeIdx}
                  grade={grade}
                  gradeIdx={gradeIdx}
                  isCollapsed={isCollapsed}
                  isAnimating={isAnimating}
                  userCompetencies={userCompetencies}
                  currentGrade={currentGrade}
                  nextGrade={nextGrade}
                  onToggleCollapse={toggleGradeCollapse}
                  competencyMapId={competencyMapId}
                  hideProgress={hideGradeProgress}
                />
              );
            })}

            {/* Остальные строки: свимлейн + компетенции по грейдам */}
            {data.swimlanes.map((swimlane: Swimlane, swimlaneIndex: number) => {
              const isSwimlaneCollapsed = collapsedSwimlanes.has(swimlaneIndex);
              return [
                <Group
                  className="swimlane-title"
                  key={swimlaneIndex}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : '#f8fafc',
                    borderRadius: '6px',
                    border: isDark ? '1px solid var(--mantine-color-dark-4)' : '1px solid #e2e8f0',
                  }}
                  onClick={() => toggleSwimlaneCollapse(swimlaneIndex)}
                >
                  <Group
                    justify="flex-start"
                    style={{
                      gap: 4,
                      display: 'flex',
                      padding: '8px 12px',
                      paddingRight: 0,
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      borderRadius: '6px 0 0 6px',
                      backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : '#f8fafc',
                    }}
                  >
                    <IconChevronRight
                      size={14}
                      color={isDark ? 'var(--mantine-color-gray-3)' : '#868e96'}
                      style={{
                        flexShrink: 0,
                        transform: isSwimlaneCollapsed ? 'rotate(90deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    />
                    <Text
                      size="sm"
                      fw={600}
                      style={{ color: isDark ? 'var(--mantine-color-gray-2)' : undefined }}
                    >
                      <span>{swimlane.title}</span>
                    </Text>
                  </Group>
                </Group>,
                <SwimlaneTransition
                  key={`swimlane-content-${swimlaneIndex}`}
                  swimlaneIndex={swimlaneIndex}
                  isCollapsed={isSwimlaneCollapsed}
                >
                  <div
                    style={{ display: 'grid', gridTemplateColumns, gap: 16, alignItems: 'start' }}
                  >
                    {data.grades.map((grade, gradeIdx) => {
                      const isCollapsed = collapsedGrades.has(gradeIdx);
                      const isVisible = visibleGrades.has(gradeIdx);
                      const comps = grade.competencies
                        .filter((comp) => comp.swimlaneId === swimlane._id)
                        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

                      if (isCollapsed) {
                        // Для свернутых грейдов показываем только пустой контейнер
                        return (
                          <div
                            key={gradeIdx}
                            style={{
                              minHeight: 64,
                              padding: 8,
                              paddingBottom: 0,
                              border: isDark
                                ? '1px solid var(--mantine-color-dark-4)'
                                : '1px solid #ececec',
                              borderRadius: 8,
                              background: isDark ? 'var(--mantine-color-dark-6)' : '#fafbfc',
                              minWidth: 60,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: isDark ? 'var(--mantine-color-gray-3)' : '#999',
                              fontSize: '12px',
                            }}
                          >
                            —
                          </div>
                        );
                      }

                      if (!isDragDropEnabled && !comps.length) return <div />;

                      return (
                        <Droppable
                          key={gradeIdx}
                          droppableId={`${gradeIdx}__${swimlane._id}`}
                          direction="vertical"
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{
                                minHeight: 64,
                                padding: 8,
                                paddingBottom: 0,
                                border: isDark
                                  ? '1px solid var(--mantine-color-dark-4)'
                                  : '1px solid #ececec',
                                borderRadius: 8,
                                background: isDark ? 'var(--mantine-color-dark-6)' : '#fafbfc',
                                fontSize: 13,
                                minWidth: 220,
                                display: 'flex',
                                flexDirection: 'column',
                                opacity: isVisible ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out',
                              }}
                            >
                              {comps.map((comp, visibleIndex) => {
                                const competencyCard = (
                                  <CompetencyCard
                                    comp={comp}
                                    gradeIdx={gradeIdx}
                                    swimlane={swimlane}
                                    onClick={() => setDrawer({ comp, gradeIdx, swimlane })}
                                    dragProps={{}}
                                    dragHandleProps={{}}
                                    isDragging={false}
                                    userHas={userCompetencies.some(
                                      (userComp) => userComp.competencyId === comp._id,
                                    )}
                                    userCompetency={userCompetencies.find(
                                      (userComp) => userComp.competencyId === comp._id,
                                    )}
                                    isDragDropEnabled={isDragDropEnabled}
                                    cardSize={cardSize}
                                    isRequired={comp.isRequired}
                                    onEdit={
                                      isAdmin
                                        ? () => {
                                            // Находим компетенцию с полем isRequired в competencyMapData
                                            const competencyWithRequired = competencyMapData?.grades
                                              .flatMap((grade) => grade.competencies)
                                              .find((c) => c._id === comp._id);

                                            setEditingCompetency(competencyWithRequired || comp);
                                          }
                                        : undefined
                                    }
                                  />
                                );

                                return isDragDropEnabled ? (
                                  <Draggable
                                    key={comp._id}
                                    draggableId={comp._id}
                                    index={visibleIndex}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...provided.draggableProps.style,
                                          marginBottom: 8,
                                        }}
                                      >
                                        {React.cloneElement(competencyCard, {
                                          dragProps: provided.draggableProps,
                                          dragHandleProps: provided.dragHandleProps,
                                          isDragging: snapshot.isDragging,
                                        })}
                                      </div>
                                    )}
                                  </Draggable>
                                ) : (
                                  <div key={comp._id} style={{ marginBottom: 8 }}>
                                    {competencyCard}
                                  </div>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      );
                    })}
                  </div>
                </SwimlaneTransition>,
                <div key={`scroll-${swimlane._id}`} style={{ width: 16 }} />,
              ];
            })}
          </div>
        </DragDropContext>
      </div>

      <CompetencyDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        grades={data.grades.map((g) => g.title)}
        userCompetencies={userCompetencies}
        competencyMapId={competencyMapId}
        currentUser={currentUser}
        hideStatusAndActions={hideStatusAndActions}
      />

      {/* Модалка редактирования компетенции */}
      <CompetencyCreateModal
        opened={!!editingCompetency}
        onClose={() => setEditingCompetency(null)}
        swimlanes={data?.swimlanes}
        grades={data?.grades}
        editingCompetency={editingCompetency}
        competencyMapId={competencyMapId}
        onRemoveFromMap={() => {
          setEditingCompetency(null);
          // Обновляем локальное состояние, убирая компетенцию из всех грейдов
          if (editingCompetency) {
            const updatedData = { ...data };
            updatedData.grades.forEach((grade) => {
              if (grade.competencies) {
                grade.competencies = grade.competencies.filter(
                  (comp) => comp._id !== editingCompetency._id,
                );
              }
            });
            setData(updatedData);
          }
        }}
      />
    </div>
  );
};
